import {
  FC,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
  RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Chat, MessageType } from '../Chat';
import { CHAT_BOT } from 'queries/query-keys';
import MachineLearningService from 'services/MachineLearningService';
import { ChatSessionFromAPI, ChatStreamType } from './types';
import { QuickResponseData } from '../QuickResponses';
import { chatMessageMapper } from './utils';
import { deferExecution } from 'utility/defer';
import { useChatBotEventStream } from 'ts-components/Conversation/useChatBotEventStream';
import { v4 } from 'uuid';
import { BUSINESS_BUILDER_BUTTONS } from 'ts-components/Chat/Controls';

export const ADD_MORE_DETAILS_OPTION = 'Add more details';

type ChatProps = {
  header?: string;
  showLogo?: boolean;
  headerLogoUrl?: string | null;
  chatSessionId?: string;
  fetchSession: (id?: string) => Promise<ChatSessionFromAPI>;
  chatButtons?: typeof BUSINESS_BUILDER_BUTTONS;
  chatStream?: ChatStreamType;
  onCompleteSession: (completeSessionId: string) => void;
  getStreamingEndpoint: (id: string) => string;
  onDeleteSession?: () => void;
  completeSessionLoading?: boolean;
  newChatSessionLoading?: boolean;
  errorMessage?: string;
  stopRefetchAfterDelete?: boolean;
  forceSessionRefetch?: boolean;
  disableSessionRefetch?: () => void;
  entityName?: string;
  eventListenerNames: string[];
  generationVarToMessageHeaderMapper?: Record<string, string>;
  trackEvent?: (event: string, payload?: any) => void;
  triggerResend?: boolean;
  mockSession?: Array<any>;
  onMockedSessionInteraction?: (
    messageToSend: string,
    resetting?: boolean
  ) => Promise<void>;
  sampleIdeas?: QuickResponseData;
  enableQuickActionButtons?: boolean;
  hideQuickActionsButtons?: boolean;
  alwaysShowStartOver?: boolean;
  headerMenuOptions?: Array<{ value: string; label: string }>;
  showOneShotGenerationButton?: boolean;
  disableOneShotGenerationButton?: boolean;
  showChatButton?: boolean;
  disableChatButton?: boolean;
  oneShotOnly?: boolean;
  onInitialMessageLoading?: () => void;
  onStartOver?: () => void;
  handleHeaderMenuOptionSelect?: ({ value }: { value: string }) => void;
  renderMarkdown?: boolean;
  hideRefresh?: boolean;
  hideLoadingSpinner?: boolean;
  disabledSend?: boolean;
  disableMessageAnimation?: boolean;
  onCopyMessage?: (messageContent: string) => void;
  onLikeMessage?: () => void;
  onDislikeMessage?: () => void;
  onRefreshMessage?: () => void;
  onOneShotGenerationClick?: () => void;
  onChatButtonClick?: (message: string) => void;
  chatButtonpPrependedMessage?: string;
  placeholderOverride?: string;
  messageHistoryRef?: RefObject<HTMLDivElement>;
  shouldFocusInput?: boolean;
  skipMessageQueue?: boolean;
  clearMessageQueue?: boolean;
  hideScrollbar?: boolean;
  showFixedHeight?: boolean;
  disableTextAreaMessage?: string;
};

export const ChatBot: FC<ChatProps> = ({
  header,
  showLogo = false,
  headerLogoUrl,
  chatSessionId = '',
  fetchSession,
  chatButtons = BUSINESS_BUILDER_BUTTONS,
  onCompleteSession,
  getStreamingEndpoint,
  onDeleteSession,
  completeSessionLoading,
  newChatSessionLoading = false,
  errorMessage,
  stopRefetchAfterDelete = false,
  forceSessionRefetch, // only to be used to force a refetch at end of session
  disableSessionRefetch,
  showOneShotGenerationButton,
  disableOneShotGenerationButton,
  showChatButton,
  disableChatButton,
  oneShotOnly,
  entityName,
  eventListenerNames,
  generationVarToMessageHeaderMapper = {},
  trackEvent,
  mockSession,
  onMockedSessionInteraction,
  sampleIdeas,
  enableQuickActionButtons,
  hideQuickActionsButtons,
  alwaysShowStartOver,
  headerMenuOptions,
  handleHeaderMenuOptionSelect,
  renderMarkdown = true,
  hideRefresh,
  hideLoadingSpinner = false,
  disabledSend = false,
  disableMessageAnimation = false,
  onInitialMessageLoading,
  onStartOver,
  onCopyMessage,
  onLikeMessage,
  onDislikeMessage,
  onRefreshMessage,
  onChatButtonClick,
  chatButtonpPrependedMessage,
  placeholderOverride,
  messageHistoryRef,
  shouldFocusInput,
  skipMessageQueue = false,
  clearMessageQueue = false,
  hideScrollbar = false,
  showFixedHeight = true,
  disableTextAreaMessage,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [messageSendQueue, setMessageSendQueue] = useState<string[]>([]);
  const [selectedQuickResponses, setSelectedQuickResponses] = useState<
    { id: string; value: string }[]
  >([]);
  const [isTransitioningFromMock, setIsTransitioningFromMock] = useState(false);

  const { sendMessageWithEventStream, isStreaming, abortStream } =
    useChatBotEventStream({
      chatSessionId,
      eventListenerNames,
      generationVarToMessageHeaderMapper,
      onInitialMessageLoading,
    });

  const resetSendQueue = useCallback(() => {
    setMessageSendQueue([]);
    onMockedSessionInteraction?.('', true);
  }, [onMockedSessionInteraction]);

  // Clear message queue when parent requests it (e.g., when transitioning from one-shot flow to real chat)
  useEffect(() => {
    if (clearMessageQueue && messageSendQueue.length > 0) {
      setMessageSendQueue([]);
    }
  }, [clearMessageQueue, messageSendQueue.length, messageSendQueue]);

  const addMoreDetailsSelected = useMemo(
    () =>
      Boolean(
        selectedQuickResponses.find(
          (pill) => pill.value === ADD_MORE_DETAILS_OPTION
        )
      ),
    [selectedQuickResponses]
  );

  const fetchChatSession = useCallback(async () => {
    try {
      let results;
      if (chatSessionId) {
        results = await fetchSession(chatSessionId);
      } else {
        results = await fetchSession();
      }
      const { messages, answers, alert, answer_mode, last_flow } =
        chatMessageMapper(results.messages, generationVarToMessageHeaderMapper);
      return {
        ...results,
        messages,
        answers,
        answer_mode,
        alert,
        last_flow,
      };
    } catch (e) {
      return {
        messages: [],
      };
    }
  }, [fetchSession, chatSessionId, generationVarToMessageHeaderMapper]);

  const {
    data: chatSession,
    refetch,
    isLoading: chatSessionLoading,
  } = useQuery({
    queryKey: CHAT_BOT.CHAT_SESSION(chatSessionId),
    queryFn: fetchChatSession,
    enabled:
      (!mockSession && !newChatSessionLoading) ||
      (chatSessionId !== '' && Boolean(mockSession)),
  }) as any;

  useEffect(() => {
    if (forceSessionRefetch) {
      refetch();
      deferExecution(() => disableSessionRefetch?.());
    }
  }, [refetch, forceSessionRefetch, disableSessionRefetch]);

  useEffect(() => {
    if (mockSession && chatSessionId) {
      setIsTransitioningFromMock(true);
    }
  }, [mockSession, chatSessionId, isTransitioningFromMock]);

  const deleteChatSessionMutation = useMutation({
    mutationFn: async () => {
      if (!chatSession || !('id' in chatSession) || !onDeleteSession) {
        return;
      }
      return onDeleteSession();
    },
    onMutate: () => {
      abortStream();

      queryClient.setQueryData(
        CHAT_BOT.CHAT_SESSION(chatSessionId),
        (prev: {
          messages: MessageType[];
          answers: string[];
          alert: string;
        }): {
          messages: MessageType[];
          answers: string[];
          alert: string;
        } => {
          return {
            ...prev,
            messages: [],
            answers: [],
            alert: '',
          };
        }
      );
    },
    onSuccess: () => {
      onStartOver?.();
    },
    onSettled: () => {
      resetSendQueue();
      if (!stopRefetchAfterDelete) {
        deferExecution(() =>
          queryClient.invalidateQueries({
            queryKey: CHAT_BOT.CHAT_SESSION(chatSessionId),
          })
        );
      }
    },
  });

  const handleAlert = useCallback(async () => {
    if (!chatSession || !('id' in chatSession)) {
      throw new Error('Chat session ID is undefined');
    }
    const response = await MachineLearningService.cancelChatSession(
      chatSession?.id
    );
    return response ? response.data : [];
  }, [chatSession]);

  const handleAlertMutation = useMutation({
    mutationFn: handleAlert,
  });

  const handleSendMessage = useCallback(
    async (
      message: string,
      isCustomMessage: boolean = true,
      skipPills = true,
      dequeMessage: boolean = false
    ) => {
      // Handle message with quick response pills
      let messageWithSelectedOptions = message;
      if (selectedQuickResponses.length > 0 && !isCustomMessage && !skipPills) {
        const selectedPills = selectedQuickResponses.filter(
          (pill) => pill.value !== ADD_MORE_DETAILS_OPTION
        );
        if (message.trim() !== '' && addMoreDetailsSelected) {
          selectedPills.push({ id: 'userMessage', value: message });
        }
        messageWithSelectedOptions = selectedPills
          .map((pill) => pill.value)
          .reduce((acc, curr, index, array) => {
            if (index === 0 && array.length === 1) {
              return curr;
            }
            if (index === 0) {
              return `"${curr}"`;
            } else if (index === array.length - 1) {
              return `${acc} and "${curr}"`;
            } else {
              return `${acc}, "${curr}"`;
            }
          }, '');
      }
      const finalMessage = messageWithSelectedOptions;
      if (mockSession && !dequeMessage) {
        setMessageSendQueue((prev) => [...prev, finalMessage]);
        if (onMockedSessionInteraction && finalMessage.trim() !== '') {
          await onMockedSessionInteraction(finalMessage);
        }
        return;
      }

      if (finalMessage.trim() === '') {
        return;
      }

      const endpointConfig = {
        getStreamingEndpoint,
        getRetryStreamingEndpoint:
          MachineLearningService.getRetryStreamingEndpoint,
      };

      setSelectedQuickResponses([]);

      if (skipMessageQueue && messageSendQueue.length > 0) {
        setMessageSendQueue([]);
        return;
      }

      return await sendMessageWithEventStream(
        finalMessage,
        endpointConfig,
        chatSession?.id
      );
    },
    [
      messageSendQueue,
      skipMessageQueue,
      selectedQuickResponses,
      addMoreDetailsSelected,
      mockSession,
      onMockedSessionInteraction,
      sendMessageWithEventStream,
      getStreamingEndpoint,
      chatSession,
    ]
  );

  const handleChatSendMessage = useCallback(
    (message: string) => {
      return handleSendMessage(message, false, false);
    },
    [handleSendMessage]
  );

  const handleAutoResponseSelect = useCallback(
    (value: string, type: string, url?: string) => {
      if (url) {
        // post message to parent window to handle navigation
        window.parent?.postMessage(
          {
            source: 'kizen-ai-chat-embed',
            event: { type: 'navigateTo', url },
          },
          '*'
        );
        return;
      }
      if (type === 'action') {
        switch (value) {
          case 'START_OVER':
            deleteChatSessionMutation.mutate();
            break;
          case 'REVIEW_PLAN':
            handleSendMessage(
              chatButtons.REVIEW_PLAN.message(entityName),
              true
            );
            break;
          case 'REFINE_PLAN':
            handleSendMessage(
              chatButtons.REFINE_PLAN.message(entityName),
              true
            );
            break;
          case 'BUILD':
            onCompleteSession(
              chatSessionId !== '' ? chatSessionId : chatSession?.id
            );
            break;
        }
        return;
      }
      if (type === 'single_select') {
        handleSendMessage(value);
      } else if (selectedQuickResponses.find((pill) => pill.value === value)) {
        setSelectedQuickResponses(
          selectedQuickResponses.filter((pill) => pill.value !== value)
        );
        return;
      } else if (value === ADD_MORE_DETAILS_OPTION && type === 'multi_select') {
        setSelectedQuickResponses((prev) => [
          ...prev,
          { id: v4(), value: value },
        ]);
        return;
      } else {
        setSelectedQuickResponses((prev) => [
          ...prev,
          { id: v4(), value: value },
        ]);
      }
    },
    [
      selectedQuickResponses,
      handleSendMessage,
      entityName,
      onCompleteSession,
      chatSession,
      chatSessionId,
      chatButtons,
      deleteChatSessionMutation,
    ]
  );

  const chatSessionQueueKey = chatSession?.id;
  const hasDequeued = useRef(false);

  useEffect(() => {
    if (
      chatSessionQueueKey &&
      messageSendQueue.length > 0 &&
      !hasDequeued.current
    ) {
      hasDequeued.current = true;
      handleSendMessage(messageSendQueue[0], true, true, true).finally(() => {
        resetSendQueue();
      });
    }
  }, [
    queryClient,
    chatSessionId,
    chatSessionQueueKey,
    messageSendQueue,
    handleSendMessage,
    resetSendQueue,
  ]);

  const alert: string = useMemo(() => {
    if (chatSession && 'alert' in chatSession) {
      return typeof chatSession.alert === 'string' ? chatSession.alert : '';
    }
    return '';
  }, [chatSession]);

  const chatMessagePlaceHolder = useMemo(() => {
    if (placeholderOverride) {
      return placeholderOverride;
    }
    if (chatSession?.ready_for_action) {
      return t('Refine Your Strategic Plan Further');
    } else if (chatSession && chatSession.answer) {
      return t('Custom Answer');
    } else {
      return t('Send a Message');
    }
  }, [placeholderOverride, chatSession, t]);

  const messages = useMemo(() => {
    if (mockSession) {
      return mockSession;
    }

    if (chatSession && 'messages' in chatSession) {
      return chatSession.messages;
    }

    return [];
  }, [chatSession, mockSession]);

  const isRefining = useMemo(() => {
    return messages?.[messages.length - 1]?.isResponseRefining;
  }, [messages]);

  const isLoadingMessagePresent = useMemo(() => {
    return (
      messages?.[messages.length - 1]?.isResponseLoading ||
      messages?.[messages.length - 1]?.isResponseRefining ||
      isStreaming
    );
  }, [messages, isStreaming]);

  const questionQuickResponseData: QuickResponseData = useMemo(() => {
    if (chatSession?.answers && chatSession?.answers?.length > 0) {
      const filteredAnswers = chatSession?.answers.filter(
        (answer: string) => 'Other (please specify)' !== answer
      );

      if (chatSession.answer_mode === 'multi_select') {
        filteredAnswers.push(ADD_MORE_DETAILS_OPTION);
      }
      return {
        options: filteredAnswers,
        selectType: chatSession.answer_mode,
      };
    }
    return { options: [], selectType: '' };
  }, [chatSession]);

  const showSkipQuestionsButton: boolean = useMemo(() => {
    return Boolean(
      chatSession &&
        chatSession?.answers?.length > 0 &&
        !chatSession?.ready_for_action
    );
  }, [chatSession]);

  const actionQuickResponseData: QuickResponseData = useMemo(() => {
    if (
      !enableQuickActionButtons ||
      chatSessionLoading ||
      newChatSessionLoading ||
      deleteChatSessionMutation.isPending
    ) {
      return { options: [], selectType: 'action' };
    }

    const actions = [];
    if (messages.length > 1 || alwaysShowStartOver) {
      setSelectedQuickResponses([]);
      actions.push({
        label: chatButtons.START_OVER.buttonText(t),
        value: 'START_OVER',
      });
    }

    if (chatSession?.ready_for_action && !isLoadingMessagePresent) {
      setSelectedQuickResponses([]);
      actions.push({
        label: chatButtons.REFINE_PLAN.buttonText(t),
        value: 'REFINE_PLAN',
      });
    }

    if (showSkipQuestionsButton && !isLoadingMessagePresent) {
      setSelectedQuickResponses([]);
      actions.push({
        label: chatButtons.REVIEW_PLAN.buttonText(t),
        value: 'REVIEW_PLAN',
      });
    }

    if (chatSession?.last_flow && chatSession?.last_flow?.next_actions) {
      setSelectedQuickResponses([]);
      chatSession.last_flow.next_actions.forEach((action: any) => {
        actions.push({
          label: action.label,
          value: action.url,
          url: action.url,
        });
      });
    }

    if (chatSession?.ready_for_action && !isLoadingMessagePresent) {
      setSelectedQuickResponses([]);
      actions.push({
        label: entityName
          ? t('Build My {{entityName}}', { entityName })
          : t('Build'),
        value: 'BUILD',
      });
    }

    return { options: actions, selectType: 'action' };
  }, [
    alwaysShowStartOver,
    isLoadingMessagePresent,
    chatSessionLoading,
    enableQuickActionButtons,
    messages.length,
    chatSession?.last_flow,
    chatSession?.ready_for_action,
    showSkipQuestionsButton,
    entityName,
    newChatSessionLoading,
    deleteChatSessionMutation.isPending,
    chatButtons,
    t,
  ]);

  const quickResponsesHeader = useMemo(() => {
    if (
      enableQuickActionButtons &&
      actionQuickResponseData.options.length > 0 &&
      questionQuickResponseData.options.length === 0
    ) {
      return t('ACTIONS');
    }

    if (
      messages.length === 1 &&
      sampleIdeas &&
      sampleIdeas?.options?.length > 0
    ) {
      return t('SAMPLE IDEAS (CHOOSE ONE OR TYPE YOUR OWN)');
    }

    if (questionQuickResponseData.selectType === 'single_select') {
      return t('QUICK RESPONSES (CHOOSE ONE OR TYPE YOUR OWN)');
    }

    return t('QUICK RESPONSES (CHOOSE MULTIPLE OR TYPE YOUR OWN)');
  }, [
    sampleIdeas,
    enableQuickActionButtons,
    actionQuickResponseData.options.length,
    questionQuickResponseData.options.length,
    questionQuickResponseData.selectType,
    messages.length,
    t,
  ]);

  const finalQuickResponseData: QuickResponseData = useMemo(() => {
    if (
      hideQuickActionsButtons ||
      newChatSessionLoading ||
      completeSessionLoading ||
      chatSessionLoading ||
      deleteChatSessionMutation.isPending
    ) {
      return { options: [], selectType: 'action' };
    }

    if (
      enableQuickActionButtons &&
      actionQuickResponseData.options.length > 0 &&
      questionQuickResponseData.options.length === 0
    ) {
      return actionQuickResponseData;
    }

    if (
      messages.length <= 1 &&
      sampleIdeas &&
      sampleIdeas?.options?.length > 0
    ) {
      return sampleIdeas || { options: [], selectType: 'single_select' };
    }
    return questionQuickResponseData;
  }, [
    chatSessionLoading,
    newChatSessionLoading,
    completeSessionLoading,
    messages,
    enableQuickActionButtons,
    hideQuickActionsButtons,
    actionQuickResponseData,
    questionQuickResponseData,
    sampleIdeas,
    deleteChatSessionMutation.isPending,
  ]);

  return (
    <Chat
      header={header}
      showLogo={showLogo}
      headerLogoUrl={headerLogoUrl}
      trackEvent={trackEvent}
      entityName={entityName}
      messages={messages}
      quickResponseData={finalQuickResponseData}
      quickResponsesHeader={quickResponsesHeader}
      selectedQuickResponses={selectedQuickResponses}
      onQuickResponseSelect={handleAutoResponseSelect}
      actionsToScrollUp={['REFINE_PLAN']}
      actionsToScrollDown={['BUILD']}
      alert={alert}
      errorMessage={errorMessage}
      onSendMessage={handleChatSendMessage}
      onCreateNewChat={() => deleteChatSessionMutation.mutate()}
      onCompleteSession={() => {
        onCompleteSession(
          chatSessionId !== '' ? chatSessionId : chatSession?.id
        );
      }}
      onAlert={handleAlertMutation.mutate}
      isCompleteSessionLoading={completeSessionLoading || false}
      isLoadingNewSession={
        (messages.length === 0 && !errorMessage) ||
        deleteChatSessionMutation.isPending ||
        (!mockSession && chatSessionLoading) ||
        newChatSessionLoading
      }
      renderMarkdown={renderMarkdown}
      hideRefresh={hideRefresh}
      hideLoadingSpinner={hideLoadingSpinner}
      disableMessageAnimation={disableMessageAnimation}
      isMessageResponseLoading={isLoadingMessagePresent}
      showCompleteSessionButton={chatSession?.ready_for_action}
      disableCompeteSessionButton={chatSessionLoading || isStreaming}
      disableSkipQuestionsButton={chatSessionLoading || isStreaming}
      disableSendMessageButton={isStreaming || disabledSend}
      showSkipQuestionsButton={showSkipQuestionsButton}
      showOneShotGenerationButton={showOneShotGenerationButton}
      disableOneShotGenerationButton={disableOneShotGenerationButton}
      showChatButton={showChatButton}
      disableChatButton={disableChatButton}
      oneShotOnly={oneShotOnly}
      showAskMoreQuestionsButton={
        chatSession?.ready_for_action &&
        questionQuickResponseData.options.length === 0
      }
      placeholder={chatMessagePlaceHolder}
      showFixedHeight={
        !isRefining &&
        !chatSession?.ready_for_action &&
        !isStreaming &&
        showFixedHeight
      }
      hideControllerButtons={enableQuickActionButtons}
      headerMenuOptions={headerMenuOptions}
      handleHeaderMenuOptionSelect={handleHeaderMenuOptionSelect}
      onCopyMessage={onCopyMessage}
      onLikeMessage={onLikeMessage}
      onDislikeMessage={onDislikeMessage}
      onRefreshMessage={onRefreshMessage}
      onChatButtonClick={
        onChatButtonClick
          ? async (message: string) => {
              const messageToSend = chatButtonpPrependedMessage
                ? `${chatButtonpPrependedMessage}${message}`
                : message;
              setMessageSendQueue([messageToSend]);
              await onChatButtonClick(messageToSend);
            }
          : undefined
      }
      messageHistoryRef={messageHistoryRef}
      shouldFocusInput={shouldFocusInput}
      disableScrollToBottom={isTransitioningFromMock}
      hideScrollbar={hideScrollbar}
      disableTextAreaMessage={disableTextAreaMessage}
    />
  );
};
